import React, { useEffect, useRef } from 'react'
import {
  Search,
  FileText,
  Zap,
  HelpCircle,
  TrendingUp,
  BarChart3,
  Trash2,
  Plus,
  Target,
  Layers,
  BookOpen,
  Calendar,
  ShieldAlert,
  UserSearch,
} from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'

export type SlashCommand = {
  name: string
  label: string
  description: string
  icon: React.ElementType
  prompt: string
  badge?: string
  action?: 'clear' | 'new'
}

export const SLASH_COMMANDS: SlashCommand[] = [
  {
    name: '/email',
    label: '/email',
    description: 'Draft a personalized cold outreach email with value hook & CTA',
    icon: FileText,
    badge: 'Outreach',
    prompt: `Draft a personalized cold outreach email:
- Prospect / Target: [Name / Company]
- Core Value Proposition: [e.g. Automate prospect research & save 10+ hrs/wk]
- Key Pain Point: [e.g. Low outbound reply rates & stale CRM data]
- Call to Action: [e.g. 15-minute introductory call next Tuesday]
- Tone: Consultative, concise, high-relevance`,
  },
  {
    name: '/qualify',
    label: '/qualify',
    description: 'Qualify a lead against ICP & BANT criteria (Budget, Authority, Need, Timeline)',
    icon: Target,
    badge: 'BDR Workflow',
    prompt: `Qualify the following prospect against ICP and BANT criteria:
- Prospect / Account: [Target Lead or Company Name]
- Budget: [Estimated budget range or funding stage]
- Authority: [Decision-maker title & buying committee members]
- Need: [Core technical & operational pain points]
- Timeline: [Target evaluation & purchase timeframe, e.g. Q4]
- Deliverable: ICP fit score (1-100), BANT breakdown matrix, and recommended next action`,
  },
  {
    name: '/enrich',
    label: '/enrich',
    description: 'Research CRM records and fill in their missing fields (you approve before anything is written)',
    icon: UserSearch,
    badge: 'Enrichment',
    prompt: `Enrich this CRM record:
- Contact or company: [Full Name, email address, or company name as it appears in the CRM]
- Research on the public web and propose values for the CRM fields that are currently empty — person facts on the contact, company facts on the company record.
- Show me each proposed field grouped by record, with its current value, the proposed value, your confidence, and the source URL (and say which sources are the company's own vs. a data directory).
- Do not write anything to the CRM until I tell you which fields to apply.`,
  },
  {
    name: '/sequence',
    label: '/sequence',
    description: 'Create a 5-step multi-touch outreach cadence (Email, LinkedIn, Phone)',
    icon: Layers,
    badge: 'Cadence',
    prompt: `Build a 5-touch multi-channel outbound cadence:
- Target Account & Persona: [Company Name, Persona / Job Title]
- Outreach Goal: Secure a 20-minute discovery call
- Touch 1 (Day 1): Hyper-personalized cold email with pain-point hook
- Touch 2 (Day 3): LinkedIn connection request + soft engagement message
- Touch 3 (Day 7): Follow-up email featuring case study proof & ROI metrics
- Touch 4 (Day 10): Cold call script & concise voicemail framework
- Touch 5 (Day 14): Value-add breakup email with industry benchmark report`,
  },
  {
    name: '/dossier',
    label: '/dossier',
    description: 'Generate an executive prospect research dossier',
    icon: BookOpen,
    badge: 'Intelligence',
    prompt: `Generate an executive prospect research dossier for:
- Target Account: [Company Name]
- Target Contact: [Full Name & Title]
- Required Sections:
  1. Executive Summary & Company Business Model
  2. Recent Growth Signals, Hiring Trends & Funding Rounds
  3. Technology Stack, CRM & Tooling Insights
  4. Decision-Maker Org Mapping & Key Stakeholders
  5. Recommended Strategic Entry Angles & Talking Points`,
  },
  {
    name: '/battlecard',
    label: '/battlecard',
    description: 'Generate a competitive sales battlecard with landmines & talk-tracks',
    icon: Zap,
    badge: 'Competitive',
    prompt: `Generate a competitive sales battlecard:
- Primary Competitor: [Competitor Name]
- Our Solution: Zeph AI Sales Assistant
- Deliverables:
  1. Quick Pitch & Competitive Advantage
  2. Feature Comparison & Key Differentiators
  3. Landmines to Lay in Discovery Calls
  4. Common Competitor Traps & How to Counter Them
  5. Customer Win Stories & Migration Proof Points`,
  },
  {
    name: '/meeting-prep',
    label: '/meeting-prep',
    description: 'Prepare a discovery call briefing with probing questions',
    icon: Calendar,
    badge: 'Meeting',
    prompt: `Prepare a discovery call preparation brief:
- Prospect / Account: [Contact Name, Job Title, Company]
- Meeting Objectives: [e.g. Validate budget, identify Champion, assess pain points]
- Agenda (25 Mins):
  1. Rapport & Context Setting (3 mins)
  2. Discovery Questions on Current Workflow & Gaps (12 mins)
  3. Tailored Zeph Solution Preview (7 mins)
  4. Next Steps & Mutual Action Plan (3 mins)
- 4 Probing Discovery Questions to Uncover Budget & Pain:`,
  },
  {
    name: '/objection',
    label: '/objection',
    description: 'Generate counter-arguments and talk-tracks for sales objections',
    icon: ShieldAlert,
    badge: 'Sales Ops',
    prompt: `Provide an objection-handling talk-track:
- Objection Raised: "[e.g. We already use HubSpot/Outreach, We don't have budget this quarter]"
- Persona: [e.g. VP of Sales, Head of RevOps]
- Include:
  1. Empathize & Validate Response
  2. Reframing Perspective
  3. Proof Point / Data Metric
  4. Pivot Question to Regain Momentum`,
  },
  {
    name: '/leads',
    label: '/leads',
    description: 'Find leads matching your ICP profile',
    icon: Search,
    badge: 'Lead Gen',
    prompt: `Find high-fit leads matching our Ideal Customer Profile (ICP):
- Target Industry: [e.g. B2B SaaS, Logistics Tech, Healthcare]
- Company Headcount: [e.g. 50-500 employees]
- Target Titles: [e.g. VP Sales, Head of Revenue Operations]
- Geographic Focus: [e.g. North America / United States]
- Prioritization: Accounts displaying active hiring or expansion buying signals`,
  },
  {
    name: '/research',
    label: '/research',
    description: 'Deep research a prospect or target company',
    icon: HelpCircle,
    badge: 'Research',
    prompt: `Research target company [Company Name or Website URL]:
- Analyze recent product launches, press releases, executive hiring, and company strategic goals for outreach context`,
  },
  {
    name: '/pipeline',
    label: '/pipeline',
    description: 'Show pipeline & deal status overview',
    icon: TrendingUp,
    badge: 'Pipeline',
    prompt: `Provide a comprehensive pipeline health analysis:
- Highlight deals requiring attention (stagnant > 14 days)
- Identify pipeline bottlenecks by stage
- Recommend immediate follow-up tasks to advance stalled opportunities`,
  },
  {
    name: '/forecast',
    label: '/forecast',
    description: 'Generate BDR quarterly forecast report',
    icon: BarChart3,
    badge: 'Reporting',
    prompt: `Generate a BDR quarterly sales forecast report:
- Projected meetings booked vs team quarterly quota
- Weighted stage conversion probabilities
- Pipeline risk assessment and upside opportunity scenarios`,
  },
  {
    name: '/clear',
    label: '/clear',
    description: 'Clear conversation history',
    icon: Trash2,
    badge: 'Action',
    prompt: '',
    action: 'clear',
  },
  {
    name: '/new',
    label: '/new',
    description: 'Start a new conversation',
    icon: Plus,
    badge: 'Action',
    prompt: '',
    action: 'new',
  },
]

interface SlashCommandPopoverProps {
  query: string
  onSelect: (command: SlashCommand) => void
  onDismiss: () => void
  activeIndex: number
}

export const SlashCommandPopover: React.FC<SlashCommandPopoverProps> = ({
  query,
  onSelect,
  onDismiss,
  activeIndex,
}) => {
  const { currentTheme } = useTheme()
  const ref = useRef<HTMLDivElement>(null)

  const cleanQuery = query.replace(/^\//, '').toLowerCase()

  const filtered = SLASH_COMMANDS.filter((c) => {
    const cmdName = c.name.replace(/^\//, '').toLowerCase()
    return (
      cmdName.includes(cleanQuery) ||
      c.description.toLowerCase().includes(cleanQuery) ||
      (c.badge && c.badge.toLowerCase().includes(cleanQuery))
    )
  })

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onDismiss()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [onDismiss])

  useEffect(() => {
    const el = ref.current?.querySelector(`[data-index="${activeIndex}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])

  if (filtered.length === 0) return null

  return (
    <div
      ref={ref}
      className="absolute bottom-full left-0 right-0 mb-2 mx-4 max-w-[60rem] rounded-xl overflow-hidden shadow-2xl animate-in fade-in slide-in-from-bottom-2 duration-150 z-30"
      style={{
        backgroundColor: currentTheme.secondary,
        border: `1px solid ${currentTheme.border}`,
        boxShadow: currentTheme.cardShadow,
      }}
    >
      <div
        className="px-3 py-2 border-b flex items-center justify-between"
        style={{ borderColor: currentTheme.border }}
      >
        <div className="flex items-center gap-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
            BDR Workflow Commands
          </p>
          <span className="text-[11px] px-1.5 py-0.5 rounded bg-indigo-500/10 text-indigo-400 font-mono">
            {filtered.length} template{filtered.length === 1 ? '' : 's'}
          </span>
        </div>
        <div className="flex items-center gap-2 text-[10px] text-slate-500 font-mono">
          <span>↑↓ Navigate</span>
          <span>↵ Insert template</span>
          <span>Esc Close</span>
        </div>
      </div>
      <div className="max-h-72 overflow-y-auto custom-scrollbar py-1">
        {filtered.map((cmd, i) => {
          const Icon = cmd.icon
          const isActive = i === activeIndex
          return (
            <button
              key={cmd.name}
              data-index={i}
              onClick={() => onSelect(cmd)}
              className="flex items-center gap-3 w-full px-3 py-2.5 text-left transition-colors duration-100 group"
              style={{
                backgroundColor: isActive ? currentTheme.hoverBackground : 'transparent',
              }}
            >
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors"
                style={{
                  backgroundColor: isActive ? `${currentTheme.accent}33` : `${currentTheme.accent}1A`,
                }}
              >
                <Icon className="w-4 h-4" style={{ color: currentTheme.accent }} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold font-mono" style={{ color: currentTheme.text }}>
                    {cmd.label}
                  </p>
                  {cmd.badge && (
                    <span className="text-[10px] px-1.5 py-0.2 rounded font-medium bg-slate-500/15 text-slate-400">
                      {cmd.badge}
                    </span>
                  )}
                </div>
                <p className="text-xs truncate mt-0.5" style={{ color: currentTheme.mutedText }}>
                  {cmd.description}
                </p>
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}

export function getFilteredCommands(query: string): SlashCommand[] {
  const cleanQuery = query.replace(/^\//, '').toLowerCase()
  return SLASH_COMMANDS.filter((c) => {
    const cmdName = c.name.replace(/^\//, '').toLowerCase()
    return (
      cmdName.includes(cleanQuery) ||
      c.description.toLowerCase().includes(cleanQuery) ||
      (c.badge && c.badge.toLowerCase().includes(cleanQuery))
    )
  })
}
